import PropTypes from 'prop-types'
import { withTranslation } from 'server/i18n'
import { Flex, Box } from 'reflexbox'
import { NextSeo } from 'next-seo'
import { useUIDSeed } from 'react-uid'
import getConfig from 'next/config'

import layoutTheme from 'theme/layout-theme'
import PageTitle from 'components/PageTitle'
import Categories from 'components/Categories'
import BlogPostCard from 'components/BlogPostCard'
import Button from 'components/Button'
import { useMemo, Fragment } from 'react'
import { useInfiniteQuery, useQuery } from 'react-query'
import { getCategories, getPage, getPosts } from 'helpers/wordpress/wp-api'

const { publicRuntimeConfig } = getConfig()
const { CLOUDINARY_URL } = publicRuntimeConfig

const fetchPosts = async key => {
    const endCursor = key.pageParam !== undefined ? key.pageParam : ''
    const { language } = key.queryKey[1]

    const data = await getPosts(language, endCursor, '')

    return data
}

const fetchCategories = async key => {
    const { language } = key.queryKey[1]

    const categories = await getCategories(language)

    return { data: categories.categories.nodes }
}

const Blog = ({ t, categories, posts, seo, i18n }) => {
    const uid = useUIDSeed()
    const seo2 = i18n.language === 'en' ? seo : seo.translation

    const { data, status, fetchNextPage, hasNextPage } = useInfiniteQuery(
        ['posts', { language: i18n?.language ? i18n?.language.toUpperCase() : 'EN' }],
        fetchPosts,
        {
            getNextPageParam: lastPage => lastPage.posts.pageInfo.endCursor
        },
        { initialData: posts }
    )

    const { data: categoriesData } = useQuery(
        ['categories', { language: i18n?.language ? i18n?.language.toUpperCase() : 'EN' }],
        fetchCategories,
        { initialData: categories }
    )

    const newPosts = useMemo(() => (data ? [...data?.pages] : posts), [data, i18n.language])

    const newCategories = useMemo(() => (categoriesData?.data?.length > 0 ? [...categoriesData.data] : categories), [
        categoriesData,
        i18n.language
    ])

    const seoData = {
        title: `${seo2.seo.title}`,
        description: seo2.seo.metaDesc,
        canonical: `https://merkandmerk.${i18n.language === 'en' ? 'com' : 'de'}/yacht-news`,
        openGraph: {
            title: `${seo2.seo.title}`,
            description: seo2.seo.metaDesc,
            url: `https://merkandmerk.${i18n.language === 'en' ? 'com' : 'de'}/yacht-news`,
            images: [
                {
                    url: seo2.featuredImage
                        ? seo2.featuredImage.node.sourceUrl
                        : `${CLOUDINARY_URL}/v1582632369/Static/sell-image_hace64.jpg`
                }
            ]
        },
        languageAlternates: [
            {
                hrefLang: 'en',
                href: 'https://merkandmerk.com/yacht-news'
            },
            {
                hrefLang: 'de',
                href: 'https://merkandmerk.de/yacht-news'
            }
        ]
    }

    return (
        <main>
            <NextSeo {...seoData} />
            <Box theme={layoutTheme} px={20} mt={[35, null, 70, 100, 150]} mb={[35, null, 70, 80]} variant="center1280">
                <PageTitle title={seo.title} subtitle={seo2.acf.subtitle} titleTag="h1" subtitleTag="h2" />
            </Box>

            <Flex theme={layoutTheme} px={20} mt={[35, null, 70]} mb={[35, null, 70, 80]} variant="center1280">
                <Categories categories={newCategories} />
            </Flex>

            <Flex
                theme={layoutTheme}
                px={20}
                mt={[35, null, 70]}
                mb={[35, null, 70, 80]}
                variant="center1600"
                flexWrap="wrap"
                justifyContent="space-between"
                flexDirection={['column', null, 'row']}>
                {status === 'success' &&
                    newPosts?.map((page, pageIndex) => (
                        <Fragment key={uid(page)}>
                            {page.posts.nodes.map((post, index) => (
                                <BlogPostCard key={uid(post)} post={post} isFirst={index === 0 && pageIndex === 0} />
                            ))}
                        </Fragment>
                    ))}
            </Flex>

            <Box theme={layoutTheme} mb={[55, null, null, 260]} mx="auto" maxWidth={1600} textAlign="center">
                {hasNextPage && (
                    <Button onClick={() => fetchNextPage()} variation="primary rounded" white>
                        {t('load-more')}
                    </Button>
                )}
            </Box>
        </main>
    )
}

Blog.propTypes = {
    categories: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    posts: PropTypes.shape({}).isRequired,
    t: PropTypes.func.isRequired,
    seo: PropTypes.shape({
        translation: PropTypes.shape({}),
        title: PropTypes.string
    }).isRequired,
    i18n: PropTypes.oneOfType([() => null, PropTypes.object]).isRequired
}

export async function getServerSideProps({ params, req }) {
    const data = await getPosts(req.i18n.language.toUpperCase(), '', '')
    const categories = await getCategories(req.i18n.language.toUpperCase())

    // There is a Blog (Blog - DE for German) page in the WordPress admin
    // We are using data from that page to generate SEO. For other categories
    // we are using SEO data from that specific category.
    const seo = await getPage('blog')

    // Restructure posts so that we can use them as initialData with React Query
    const posts = {
        pages: [{ posts: data.posts }],
        pageParams: [null]
    }

    return {
        props: {
            categories: categories.categories.nodes,
            posts,
            seo: seo.page
        }
    }
}

export default withTranslation()(Blog)
